import { useEffect, useRef } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { useTranslate } from 'react-translate.ts';
import { ObjectSchema, StringSchema, ref as YupRef } from 'yup';
import { FormInput, FormField } from '@tcc-online/input-components';
import { Button, ButtonVariants } from '@tcc-online/common-components';
import {
    TRANSLATIONS_REQUIRED_FIELD_KEY,
    TRANSLATIONS_ROOT_KEY,
} from '../../../constants/translation.constants';
import AuthForm from '../../../pages/Auth/AuthForm/AuthForm';

const OLD_PASS = 'oldPassword';
const PASS = 'password';
const PASS_CHECK = 'passwordCheck';

type Values = {
    [OLD_PASS]: string;
    [PASS]: string;
    [PASS_CHECK]: string;
};

type ResetPasswordFormProps = {
    onSubmit: (oldPassword: string, password: string) => void;
    token?: string;
    reinit?: boolean;
    error?: string;
    requireOldPassword?: boolean;
};

const OLD_PASSWORD_KEY = 'change-password.old-password';
const PASSWORD_KEY = 'change-password.password';

export function ChangePasswordForm(props: ResetPasswordFormProps) {
    const { token, onSubmit, reinit, error, requireOldPassword } = props;
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY);
    const translateReq = useTranslate();

    return (
        <Formik<Values>
            validationSchema={validation()}
            initialValues={{
                oldPassword: token || '',
                password: '',
                passwordCheck: '',
            }}
            onSubmit={submit}
        >
            <InnerForm
                error={error}
                reinit={reinit}
                requireOldPassword={requireOldPassword}
            />
        </Formik>
    );

    function submit(values: Values) {
        const { oldPassword, password } = values;

        onSubmit(oldPassword, password);
    }

    function validation() {
        return new ObjectSchema({
            // @ts-ignore
            [OLD_PASS]: requireOldPassword
                ? new StringSchema().required(
                      translateReq(TRANSLATIONS_REQUIRED_FIELD_KEY, {
                          0: translate(OLD_PASSWORD_KEY),
                      }),
                  )
                : null,
            [PASS]: new StringSchema().required(
                translateReq(TRANSLATIONS_REQUIRED_FIELD_KEY, {
                    0: translate(PASSWORD_KEY),
                }),
            ),
            [PASS_CHECK]: new StringSchema().oneOf(
                [YupRef(PASS)],
                translate('change-password.error'),
            ),
        });
    }
}

type FormProps = {
    error?: string;
    reinit?: boolean;
    requireOldPassword?: boolean;
};

function InnerForm(props: FormProps) {
    const { reinit, requireOldPassword } = props;
    const translate = useTranslate(TRANSLATIONS_ROOT_KEY);

    const formik = useFormikContext<Values>();
    const formikRef = useRef(formik);

    useEffect(() => {
        if (reinit) {
            formikRef.current.resetForm();
        }
    }, [reinit]);

    return (
        <Form>
            <AuthForm>
                {requireOldPassword && (
                    <FormField>
                        <FormInput
                            name={OLD_PASS}
                            type="password"
                            label={translate(OLD_PASSWORD_KEY)}
                            autoComplete="off"
                        />
                    </FormField>
                )}
                <FormField>
                    <FormInput
                        name={PASS}
                        type="password"
                        label={translate(PASSWORD_KEY)}
                        autoComplete="off"
                    />
                </FormField>
                <FormField>
                    <FormInput
                        name={PASS_CHECK}
                        type="password"
                        label={translate('change-password.password-again')}
                        autoComplete="off"
                    />
                </FormField>
            </AuthForm>
            <div style={{ textAlign: 'right' }}>
                <Button type="submit" variant={ButtonVariants.positive}>
                    {translate('change-password.change')}
                </Button>
            </div>
        </Form>
    );
}
